import React, { useState } from "react";
import PropTypes from "prop-types";
import { AlertTriangle, Avatar, Badge, Button, Card, CheckCircle, EmptyState, PageHeader, Tabs, TextInput } from "@opendate-app/setlist";
import client from "../../../shared/api/client";
import { toastError, toastSuccess } from "../../../shared/toastHelper";
import { countOrUndefined } from "../../shared/components/CountBadge";
import StaffingTabs, { STAFFING_SUPPORTING_TEXT } from "./StaffingTabs";
import { errorMessages } from "./staffingFormatters";

const REQUEST_TABS = [
  { value: "timeOff", label: "Time off" },
  { value: "trades", label: "Trades and cover" },
  { value: "pickups", label: "Open shift pickups" },
  { value: "availability", label: "Availability" },
];

const EMPTY_STATES = {
  timeOff: {
    title: "No time-off requests",
    description: "Staff ask for time off from the portal. Approved time off blocks scheduling and shows as a warning in the builder.",
  },
  trades: {
    title: "No trades or covers waiting",
    description: "When someone offers a shift and a colleague claims it, it lands here for approval.",
  },
  pickups: {
    title: "No pickup requests",
    description: "When staff claim open shifts, they show up here. Turn on auto-approve in Settings to skip this step.",
  },
  availability: {
    title: "No availability changes",
    description: "Staff update their weekly availability from the portal.",
  },
};

const STATUS_BADGES = {
  pending: { color: "warning", label: "Pending" },
  open: { color: "gray", label: "Open" },
  claimed: { color: "warning", label: "Claimed · needs approval" },
  approved: { color: "success", label: "Approved" },
  denied: { color: "error", label: "Denied" },
};

const textStyle = (size, color, weight = 400) => ({ fontSize: size, lineHeight: `${size + 6}px`, color: `var(--sl-${color})`, fontWeight: weight });

export const approvedMessage = (tab, request) => {
  if (request.approvedToast) return request.approvedToast;
  if (tab === "availability") return "Availability updated. The schedule warns on shifts outside it.";
  if (request.openShift) return `Time off approved. ${request.openShift.event} now has an open ${request.openShift.position.toLowerCase()} shift. Publish to post it.`;
  return `Time off approved for ${request.firstName}.`;
};

export const deniedMessage = (request, note) => request.deniedToast || `Denied. ${request.firstName} gets a text${note.trim() ? " with your note." : "."}`;

const StatusBadge = ({ status }) => (
  <Badge color={STATUS_BADGES[status].color} size="sm">
    {STATUS_BADGES[status].label}
  </Badge>
);

StatusBadge.propTypes = { status: PropTypes.oneOf(Object.keys(STATUS_BADGES)).isRequired };

const PendingRequestCard = ({ request, note, onNoteChange, onDecide, busy }) => (
  <Card padding="md">
    <div className="d-flex flex-wrap align-items-start" style={{ gap: 16 }}>
      <Avatar size="md" initials={request.initials} alt={request.name} />
      <div className="d-flex flex-column" style={{ flex: "1 1 320px", minWidth: 0, gap: 4 }}>
        <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
          <span style={textStyle(14, "text-primary", 600)}>{request.name}</span>
          <span style={textStyle(12, "text-quaternary")}>{[request.position, request.submitted].filter(Boolean).join(" · ")}</span>
          <StatusBadge status={request.status} />
        </div>
        <div style={textStyle(16, "text-primary", 600)}>{request.title}</div>
        {request.detail && <div style={textStyle(14, "text-tertiary")}>{request.detail}</div>}
        {request.warnings.map((warning) => (
          <div
            key={warning}
            className="d-flex"
            role="status"
            style={{
              gap: 8,
              padding: "10px 12px",
              marginTop: 4,
              borderRadius: "var(--radius-md, 8px)",
              border: "1px solid var(--sl-border-warning)",
              background: "var(--sl-bg-warning-primary)",
              ...textStyle(13, "text-warning"),
            }}
          >
            <AlertTriangle width={16} height={16} style={{ flexShrink: 0, marginTop: 1 }} aria-hidden="true" />
            <span>{warning}</span>
          </div>
        ))}
        {request.takesNote !== false && (
          <div style={{ marginTop: 6, maxWidth: 520 }}>
            <TextInput
              id={`staffing-request-note-${request.key || request.id}`}
              size="sm"
              placeholder="Add a note (sent with your decision)"
              value={note}
              onChange={(event) => onNoteChange(event.target.value)}
            />
          </div>
        )}
      </div>
      <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
        <Button variant="secondary" disabled={busy} onClick={() => onDecide("deny")}>
          Deny
        </Button>
        <Button variant="primary" disabled={busy || !request.canApprove} onClick={() => onDecide("approve")}>
          {request.approveLabel || (request.warnings.length > 0 ? "Approve and open shift" : "Approve")}
        </Button>
      </div>
    </div>
  </Card>
);

const requestShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  key: PropTypes.string,
  status: PropTypes.oneOf(Object.keys(STATUS_BADGES)).isRequired,
  firstName: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  initials: PropTypes.string.isRequired,
  position: PropTypes.string,
  submitted: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  detail: PropTypes.string,
  decidedNote: PropTypes.string,
  warnings: PropTypes.arrayOf(PropTypes.string).isRequired,
  canApprove: PropTypes.bool.isRequired,
  openShift: PropTypes.shape({ event: PropTypes.string.isRequired, position: PropTypes.string.isRequired }),
  approveLabel: PropTypes.string,
  approvedToast: PropTypes.string,
  deniedToast: PropTypes.string,
  takesNote: PropTypes.bool,
  approvePath: PropTypes.string.isRequired,
  denyPath: PropTypes.string.isRequired,
});

PendingRequestCard.propTypes = {
  request: requestShape.isRequired,
  note: PropTypes.string.isRequired,
  onNoteChange: PropTypes.func.isRequired,
  onDecide: PropTypes.func.isRequired,
  busy: PropTypes.bool.isRequired,
};

const DecidedRow = ({ request, last }) => (
  <div
    className="d-flex flex-wrap align-items-center"
    style={{ gap: 12, padding: "12px 20px", borderBottom: last ? "none" : "1px solid var(--sl-border-secondary)" }}
  >
    <Avatar size="sm" initials={request.initials} alt={request.name} />
    <div className="d-flex flex-column" style={{ flex: "1 1 280px", minWidth: 0 }}>
      <span style={textStyle(14, "text-primary")}>
        <b style={{ fontWeight: 600 }}>{request.name}</b> · {request.title}
      </span>
      {request.detail && <span style={textStyle(13, "text-tertiary")}>{request.detail}</span>}
      {request.decidedNote && <span style={textStyle(13, "text-quaternary")}>Note: {request.decidedNote}</span>}
    </div>
    <StatusBadge status={request.status} />
  </div>
);

DecidedRow.propTypes = { request: requestShape.isRequired, last: PropTypes.bool.isRequired };

const RequestsInbox = ({ tabPaths, initialRequests, paths }) => {
  const [requests, setRequests] = useState(initialRequests);
  const [tab, setTab] = useState("timeOff");
  const [notes, setNotes] = useState({});
  const [busyKey, setBusyKey] = useState(null);
  const pendingTotal = REQUEST_TABS.reduce((total, { value }) => total + requests[value].pending.length, 0);
  const { pending, decided } = requests[tab];
  const empty = EMPTY_STATES[tab];

  const decide = (request, decision) => {
    const key = `${tab}-${request.key || request.id}`;
    const note = notes[key] || "";
    setBusyKey(key);
    client
      .patch(decision === "approve" ? request.approvePath : request.denyPath, { note })
      .then(() => {
        toastSuccess(decision === "approve" ? approvedMessage(tab, request) : deniedMessage(request, note));
        return client.get(paths.requests);
      })
      .then(({ data }) => setRequests(data))
      .catch((error) => toastError(errorMessages(error)[0] || "Could not save your decision."))
      .finally(() => setBusyKey(null));
  };

  return (
    <>
      <PageHeader title="Staffing" supportingText={STAFFING_SUPPORTING_TEXT} />
      <div className="mt-3">
        <StaffingTabs current="requests" paths={{ ...tabPaths, counts: { ...tabPaths.counts, requests: pendingTotal } }} />
      </div>

      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3" style={{ gap: 12 }}>
        <Tabs
          variant="button-border"
          collapseBelow={0}
          items={REQUEST_TABS.map(({ value, label }) => {
            const count = countOrUndefined(requests[value].pending.length);
            return count ? { value, label, count } : { value, label };
          })}
          value={tab}
          onChange={setTab}
        />
        {requests.venueLabel && (
          <span style={textStyle(13, "text-tertiary")}>Managers with access to {requests.venueLabel} get an email for each new request.</span>
        )}
      </div>

      {pending.length === 0 && (
        <Card padding="md">
          <EmptyState size="sm" showRings={false} icon={CheckCircle} title={empty.title} description={empty.description} />
        </Card>
      )}
      <div className="d-flex flex-column" style={{ gap: 12 }}>
        {pending.map((request) => {
          const key = `${tab}-${request.key || request.id}`;
          return (
            <PendingRequestCard
              key={key}
              request={request}
              note={notes[key] || ""}
              onNoteChange={(value) => setNotes((current) => ({ ...current, [key]: value }))}
              onDecide={(decision) => decide(request, decision)}
              busy={busyKey !== null}
            />
          );
        })}
      </div>

      {decided.length > 0 && (
        <>
          <div style={{ ...textStyle(14, "text-secondary", 600), margin: "24px 0 8px" }}>Decided</div>
          <Card padding="none">
            {decided.map((request, index) => (
              <DecidedRow key={`${tab}-${request.id}`} request={request} last={index === decided.length - 1} />
            ))}
          </Card>
        </>
      )}
    </>
  );
};

const listShape = PropTypes.shape({
  pending: PropTypes.arrayOf(requestShape).isRequired,
  decided: PropTypes.arrayOf(requestShape).isRequired,
});

export const requestsPayloadShape = PropTypes.shape({
  venueLabel: PropTypes.string,
  timeOff: listShape.isRequired,
  trades: listShape.isRequired,
  pickups: listShape.isRequired,
  availability: listShape.isRequired,
});

RequestsInbox.propTypes = {
  tabPaths: PropTypes.object.isRequired,
  initialRequests: requestsPayloadShape.isRequired,
  paths: PropTypes.shape({ requests: PropTypes.string.isRequired }).isRequired,
};

export default RequestsInbox;
